import { createMongoAbility } from '@casl/ability';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { getDefaultStore } from 'jotai';

import { abilityOverrideAtom } from '#state/atoms/ability-override-atom.js';
import { lastOpenProjectAtom } from '#state/atoms/last-open-project-atom.js';

export const Route = createFileRoute('/_authorization-gate/')({
	beforeLoad: async ({ context: { queryClient, trpc } }) => {
		// Conditionally redirect:
		// Regular users / admins -> projects page
		// Wizard operators -> wizard page

		const store = getDefaultStore();
		const lastOpenProject = store.get(lastOpenProjectAtom);
		const currentUser = await queryClient.ensureQueryData(trpc.user.getCurrent.queryOptions());

		const abilityWithoutOverride = createMongoAbility(currentUser.auth.rules);
		const abilityOverride = store.get(abilityOverrideAtom);
		const abilityOverrideAbility = abilityOverride.rules ? createMongoAbility(abilityOverride.rules) : undefined;
		const abilityWithOverride = abilityOverrideAbility ?? abilityWithoutOverride;

		// Redirect non-staff Wizard operators/admins to the Wizard page
		if (
			!currentUser.auth.isStaff &&
			(abilityWithOverride.can('manage', 'wizard') || abilityWithOverride.can('manage', 'wizardAdmin'))
		) {
			throw redirect({ to: '/wizard' });
		}

		// Check if the user has access to the org in the atom. Staff can access any org even without a membership.
		if (lastOpenProject.orgName !== null && lastOpenProject.orgName !== '') {
			// Staff can access any org, so we can redirect them directly to whatever `orgName` was in the atom
			if (currentUser.auth.isStaff) {
				throw redirect({ to: '/orgs/$orgName/projects', params: { orgName: lastOpenProject.orgName } });
			}

			// Verify that the user has access to the org in the atom
			const userMembership = currentUser.memberships.find((m) => m.groupUsername === lastOpenProject.orgName);
			if (userMembership) {
				throw redirect({ to: '/orgs/$orgName/projects', params: { orgName: lastOpenProject.orgName } });
			}

			// If no access to the org in the atom, fall back to the code below
		}

		// If no `orgName` specified in the atom, default to using the first membership from the user's memberships array
		const activeMembership = currentUser.memberships[0];
		if (!activeMembership) {
			throw new Error(
				'You are not a member of any organizations. Please contact your administrator to be added to an organization.',
			);
		}

		throw redirect({ to: '/orgs/$orgName/projects', params: { orgName: activeMembership.groupUsername } });
	},
	head: () => ({ meta: [{ title: 'Cadstrom' }] }),
});
